import { useCallback, useRef } from "react";
import toast from "react-hot-toast";
import type { DownloadProgress } from "components/common/DownloadProgressToaster";
import DownloadProgressToaster from "components/common/DownloadProgressToaster";
import { useSelector } from "utils/redux";
import { logToSentry } from "utils/sentry";

type DownloadOptions = {
  url: string;
  filename: string;
  totalBytes?: number;
  onComplete?: () => void;
  onError?: (error: Error) => void;
};

// Marker set on errors that were already reported to Sentry at a specific
// detection site, so the catch-all handler can avoid logging them twice.
type ReportedError = Error & { reportedToSentry?: true };

export const useDownloadWithProgress = () => {
  const accessToken = useSelector((state) => state.main.accessToken);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const progressRef = useRef<Map<string, DownloadProgress>>(new Map());

  const dismissDownload = useCallback((id: string) => {
    toast.dismiss(id);
    progressRef.current.delete(id);
    abortControllersRef.current.delete(id);
  }, []);

  const cancelDownload = useCallback(
    (id: string) => {
      const progress = progressRef.current.get(id);
      if (!progress) return;

      const controller = abortControllersRef.current.get(id);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(id);
      }

      // update progress to cancelled status
      const cancelledProgress = {
        ...progress,
        status: "cancelled" as const,
      };
      progressRef.current.set(id, cancelledProgress);

      // re-render toast with cancelled state
      toast.custom(
        () => (
          <DownloadProgressToaster
            download={cancelledProgress}
            onDismiss={() => dismissDownload(id)}
          />
        ),
        { id, duration: Infinity },
      );

      // auto-dismiss after 3 seconds
      setTimeout(() => {
        dismissDownload(id);
      }, 3000);
    },
    [dismissDownload],
  );

  const startDownload = useCallback(
    async (options: DownloadOptions): Promise<void> => {
      const { url, filename, totalBytes, onComplete, onError } = options;
      const downloadId = `download-${Date.now()}-${Math.random()}`;

      // create abort controller for this download
      const abortController = new AbortController();
      abortControllersRef.current.set(downloadId, abortController);

      // initialize download state
      let currentProgress: DownloadProgress = {
        id: downloadId,
        filename,
        totalBytes: totalBytes || 0,
        downloadedBytes: 0,
        percentage: 0,
        speedBytesPerSecond: 0,
        estimatedSecondsRemaining: 0,
        status: "downloading",
      };

      progressRef.current.set(downloadId, currentProgress);

      // helper to update progress and re-render toast
      const updateProgress = (updates: Partial<DownloadProgress>) => {
        currentProgress = { ...currentProgress, ...updates };
        progressRef.current.set(downloadId, currentProgress);

        // re-render toast with updated progress
        toast.custom(
          () => (
            <DownloadProgressToaster
              download={currentProgress}
              onCancel={() => cancelDownload(downloadId)}
              onDismiss={() => dismissDownload(downloadId)}
            />
          ),
          { id: downloadId, duration: Infinity },
        );
      };

      // render initial toast
      updateProgress({});

      try {
        const headers: HeadersInit = {};
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }

        const response = await fetch(url, {
          signal: abortController.signal,
          headers,
        });

        if (!response.ok) {
          const error: ReportedError = new Error(
            `Failed to fetch: ${response.statusText} (${response.status})`,
          );
          // log actual error for debugging
          logToSentry("Download failed - HTTP error", "error", {
            url,
            filename,
            status: response.status,
            statusText: response.statusText,
            errorMessage: error.message,
          });
          error.reportedToSentry = true;
          throw error;
        }

        // When ENABLE_PRESIGNED_URL_DOWNLOADS is on, /api/v1/file_proxy returns
        // {"url": "<presigned-s3-url>", "ttl_seconds": ...} as JSON instead of
        // streaming the bytes. Hand the browser the presigned URL via a click
        // — the URL's X-Amz-* query params travel with the navigation and S3
        // serves the file. We can't fetch() it from JS because the bucket
        // doesn't have CORS for our origin, and S3 rejects an Authorization
        // header on presigned URLs (auth is in the query string).
        const responseContentType = response.headers.get("content-type") || "";
        if (responseContentType.includes("application/json")) {
          const payload = (await response.json()) as { url?: string };
          if (!payload?.url) {
            throw new Error(
              "Backend returned JSON without a presigned download URL",
            );
          }
          const presignedAnchor = document.createElement("a");
          presignedAnchor.href = payload.url;
          presignedAnchor.download = filename;
          document.body.appendChild(presignedAnchor);
          presignedAnchor.click();
          document.body.removeChild(presignedAnchor);
          updateProgress({
            status: "complete",
            percentage: 100,
            downloadedBytes: totalBytes || 0,
            totalBytes: totalBytes || 0,
          });
          setTimeout(() => {
            dismissDownload(downloadId);
          }, 3000);
          onComplete?.();
          return;
        }

        const contentLength = response.headers.get("content-length");
        const total = contentLength
          ? parseInt(contentLength, 10)
          : totalBytes || 0;

        if (total > 0) {
          updateProgress({ totalBytes: total });
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No reader available");
        }

        const chunks: BlobPart[] = [];
        let receivedLength = 0;
        const startTime = Date.now();
        let lastUpdateTime = startTime;
        let lastReceivedLength = 0;
        let done = false;

        while (!done) {
          const result = await reader.read();
          done = result.done;

          if (done || !result.value) break;

          chunks.push(result.value);
          receivedLength += result.value.length;

          // calculate speed and ETA
          const now = Date.now();
          const elapsedSeconds = (now - startTime) / 1000;
          const timeSinceLastUpdate = (now - lastUpdateTime) / 1000;

          // update every 200ms or every 1MB
          if (
            timeSinceLastUpdate >= 0.2 ||
            receivedLength - lastReceivedLength >= 1024 * 1024
          ) {
            const speed =
              elapsedSeconds > 0 ? receivedLength / elapsedSeconds : 0;
            const bytesRemaining = total - receivedLength;
            const eta = speed > 0 ? bytesRemaining / speed : 0;
            const percentage =
              total > 0 ? Math.round((receivedLength / total) * 100) : 0;

            updateProgress({
              downloadedBytes: receivedLength,
              percentage,
              speedBytesPerSecond: Math.round(speed),
              estimatedSecondsRemaining: Math.round(eta),
            });

            lastUpdateTime = now;
            lastReceivedLength = receivedLength;
          }
        }

        // create blob and trigger download
        const blob = new Blob(chunks);
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);

        // mark as complete
        updateProgress({
          status: "complete",
          percentage: 100,
          downloadedBytes: receivedLength,
          totalBytes: receivedLength,
        });

        // auto-dismiss after 3 seconds
        setTimeout(() => {
          dismissDownload(downloadId);
        }, 3000);

        onComplete?.();
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          // user cancelled, already handled
          return;
        }

        // log actual error for debugging, unless it was already reported at a
        // more specific detection site (e.g. the HTTP-error branch above)
        const alreadyReported =
          error instanceof Error && (error as ReportedError).reportedToSentry;
        if (!alreadyReported) {
          logToSentry("Download error", "error", {
            filename,
            url,
            errorMessage:
              error instanceof Error ? error.message : "Unknown error",
            errorType: error instanceof Error ? error.name : typeof error,
          });
        }

        // show friendly message to user
        const friendlyMessage =
          "Unable to download file. Please try again later.";

        updateProgress({
          status: "error",
          errorMessage: friendlyMessage,
        });

        onError?.(
          error instanceof Error ? error : new Error("Download failed"),
        );
      }
    },
    [accessToken, cancelDownload, dismissDownload],
  );

  return {
    startDownload,
    cancelDownload,
  };
};
